import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { ApolloClient, DocumentNode, useApolloClient } from '@apollo/client';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';

import { downloadExcelFile } from '@utils/excel/download-json-excel-service';
import DynamicFormWidget from '@components/dynamic-form/DynamicFormWidget';
import { IBaseDrawerProps } from '@common/types';
import FormattingService from '@core/services/FormattingService';
import {
  ALL,
  exportTreatiesDrawerSections,
  exportTreatiesExcelColumnWidths,
  exportTreatiesExcelHeaderGroups,
  exportTreatiesExcelHeaders,
  exportTreatiesExcelTemplateStyles,
  IExportTreatiesFilters
} from '@common/constants';
import { useDrawerSubmission } from '@common/hooks';
import { useGetTreatiesExportLovs } from '@common/slices';
import { exportTreaties } from '@common/services';
import {
  formatForFileName,
  normalizeMulti,
  normalizeStatusesForBackend
} from './exportReinsurers.drawer';

export const formatEnumToPascalCase = (enumValue: string): string => {
  return String(enumValue)
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
};

export const normalizeTypesForBackend = (values: string[] | null) => {
  if (!values?.length) return null;

  return values.map((t) => formatEnumToPascalCase(String(t)));
};

export const fetchAllTreaties = async (
  client: ApolloClient<any>,
  query: DocumentNode,
  vars: {
    companies: string[] | null;
    types: string[] | null;
    statuses: string[] | null;
    effectiveDateFrom: any | null;
    effectiveDateTo: any | null;
    createdOnDateFrom: any | null;
    createdOnDateTo: any | null;
    keyword: string | null;
    OrderBy: any | null;
  }
) => {
  const pageSize = 500;
  let pageNumber = 1;

  const all: any[] = [];
  let hasMore = true;

  while (hasMore) {
    const { data } = await client.query({
      query,
      fetchPolicy: 'no-cache',
      variables: {
        pagination: { pageSize, pageNumber },
        companies: vars.companies,
        types: vars.types,
        statuses: vars.statuses,
        effectiveDateFrom: vars.effectiveDateFrom,
        effectiveDateTo: vars.effectiveDateTo,
        createdOnDateFrom: vars.createdOnDateFrom,
        createdOnDateTo: vars.createdOnDateTo,
        keyword: vars.keyword,
        OrderBy: vars.OrderBy
      }
    });

    const items = data?.reinsurance?.searchTreaties?.items || [];
    all.push(...items);

    hasMore = items.length === pageSize;
    pageNumber += 1;
  }

  return all;
};

export const buildDateFromTo = (range: any[]) => {
  const [start, end] = range ?? [];
  const from = start ? new Date(start).toISOString() : null;
  const to = end ? new Date(end).toISOString() : null;

  return [from, to] as const;
};
const mapTreatiesItemsToExcelData = (items: any[]) => {
  return (items ?? [])
    .map((t: any) => ({
      code: t?.code ?? '',
      name: t?.name ?? '',
      type: FormattingService.formatEnumToDisplay(t?.type) ?? t?.type ?? '',
      currency: t?.currency?.title ?? '',
      status:
        FormattingService.formatEnumToDisplay(t?.status) ?? t?.status ?? '',
      treatyEffectiveDate: t?.treatyEffectiveDate ?? '',
      treatyExpiryDate: t?.treatyExpiryDate ?? '',
      underwritingLimit: t?.underwritingLimit ?? '',
      substandardLimit: t?.substandardLimit ?? '',
      claimAdviseLimit: t?.claimAdviseLimit ?? '',
      leaderReinsurer: t?.leaderReinsurer?.fullName ?? ''
    }))
    .sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')));
};

const withAll = (options: Record<string, string>) => ({
  [ALL]: 'All',
  ...(options ?? {})
});

const companiesAsRecord = (list: any[] = []) =>
  list.reduce<Record<string, string>>((acc, item) => {
    if (item?.id) acc[item.id] = item?.title ?? item?.id;

    return acc;
  }, {});

const ExportTreatiesDrawer: React.FC<IBaseDrawerProps> = ({ onSuccess }) => {
  const client = useApolloClient();
  const { submitButtonState, formDisabled, handleSubmission } =
    useDrawerSubmission();

  const [values, setValues] = useState<IExportTreatiesFilters>({
    relatedCompany: [ALL],
    treatyType: [ALL],
    treatyStatus: [ALL],
    effectiveDate: [],
    treatyCreatedOn: []
  });

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({});
  const [sections, setSections] = useState<IDynamicFormWidgetSection[]>([]);

  const { data: lovData } = useGetTreatiesExportLovs.useQuery();

  useEffect(() => {
    if (!lovData) return;

    const relatedCompanyOptions = withAll(
      companiesAsRecord(lovData?.salesforce?.getRelatedCompanies || [])
    );

    const typeOptions = withAll(
      (lovData?.TreatyTypeList?.enumValues || []).reduce(
        (acc: any, e: any) => {
          acc[e.name] = FormattingService.formatEnumToDisplay(e.name);

          return acc;
        },
        {} as Record<string, string>
      )
    );

    const statusOptions = withAll(
      (lovData?.TreatyStatusList?.enumValues || []).reduce(
        (acc: any, e: any) => {
          acc[e.name] = FormattingService.formatEnumToDisplay(e.name);

          return acc;
        },
        {} as Record<string, string>
      )
    );

    const built = {
      RelatedCompanyList: relatedCompanyOptions,
      TreatyTypeList: typeOptions,
      TreatyStatusList: statusOptions
    };

    setLovs(built);
    setSections(exportTreatiesDrawerSections(values, built));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lovData]);

  const submitForm = async (formValues: Record<string, any>) => {
    await handleSubmission(
      async () => {
        const [data] = normalizeDynamicFormDrawerValues(sections, formValues);

        const selectedCompanies = normalizeMulti(data.relatedCompany);
        const selectedTypes = normalizeMulti(data.treatyType);
        const selectedStatuses = normalizeMulti(data.treatyStatus);

        if (
          !selectedCompanies.length ||
          !selectedTypes.length ||
          !selectedStatuses.length
        ) {
          toast.error(
            <ToastErrorMessage>Required fields are missing</ToastErrorMessage>
          );
          return false;
        }

        const companiesVar = selectedCompanies.includes(ALL)
          ? null
          : selectedCompanies;

        const typesVar = selectedTypes.includes(ALL)
          ? null
          : normalizeTypesForBackend(selectedTypes);

        const statusesVar = selectedStatuses.includes(ALL)
          ? null
          : normalizeStatusesForBackend(selectedStatuses);

        const [effectiveDateFrom, effectiveDateTo] = buildDateFromTo(
          data.effectiveDate
        );

        const createdOnRange = data.treatyCreatedOn ?? [];
        const [createdOnDateFrom, createdOnDateTo] =
          createdOnRange?.length === 2
            ? buildDateFromTo(createdOnRange)
            : [null, null];

        let fileName = 'Redbridge_Treaties';
        const pickerFrom = createdOnRange?.[0];
        const pickerTo = createdOnRange?.[1];
        if (pickerFrom && pickerTo) {
          fileName += `_${formatForFileName(pickerFrom)}_${formatForFileName(
            pickerTo
          )}`;
        }

        const treaties = await fetchAllTreaties(client, exportTreaties, {
          companies: companiesVar,
          types: typesVar,
          statuses: statusesVar,
          effectiveDateFrom,
          effectiveDateTo,
          createdOnDateFrom,
          createdOnDateTo,
          keyword: null,
          OrderBy: null
        });

        if (!treaties.length) {
          toast.error(
            <ToastErrorMessage>
              No treaties found for the selected filters
            </ToastErrorMessage>
          );
          return false;
        }

        const excelData = mapTreatiesItemsToExcelData(treaties);

        downloadExcelFile({
          data: excelData,
          filename: fileName,
          headers: exportTreatiesExcelHeaders,
          sheetName: 'Sheet1',
          groupHeaders: exportTreatiesExcelHeaderGroups,
          styles: exportTreatiesExcelTemplateStyles,
          columnWidths: exportTreatiesExcelColumnWidths,
          freezeHeaderRows: 0
        });

        toast.success(
          <ToastSuccessMessage>
            Treaties successfully exported
          </ToastSuccessMessage>
        );

        return true;
      },
      (result) => {
        if (result) onSuccess?.();
      },
      (error) => {
        console.error(error);
        toast.error(
          <ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>
        );
      }
    );
  };

  const handleChange = (
    fieldName: keyof IExportTreatiesFilters,
    value: any,
    _sectionIndex: number,
    updatedValues: IExportTreatiesFilters
  ) => {
    const newValues = { ...updatedValues, [fieldName]: value };
    setValues(newValues);
    setSections(exportTreatiesDrawerSections(newValues, lovs));
  };

  return (
    <DynamicFormWidget
      onChange={handleChange}
      sections={sections}
      disableForm={formDisabled}
      submitButtonState={submitButtonState}
      onSubmit={submitForm}
    />
  );
};

export { ExportTreatiesDrawer };
